import { getFromDataPath, reduceDataPath, removeByPath } from "../../../utility";
import { ArsTableCellType, ArsTableState, ArstActionContextmenuAction } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { arst_checkRowHeaders } from "./arst_checkRowHeaders";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";
import { computeUsedGroupingFactorIds } from "./computeUsedGroupingFactorIds";
import { containsAnalysisMethod } from "./containsAnalysisMethod";

/**
 * Removes a grouping factor node from the table body and updates row headers when required.
 *
 * @param state - Current ARS table state
 * @param action - Context-menu action describing the grouping factor cell to remove
 * @returns Updated ARS table state
 */
export const arst_removeGroupingFactor = <T>(
  state: ArsTableState<T>,
  action: ArstActionContextmenuAction<T>,
): ArsTableState<T> => {
  if (
    action.cellValue.cellType !== ArsTableCellType.ColumnGroupCell &&
    action.cellValue.cellType !== ArsTableCellType.ColumnGroupLabel &&
    action.cellValue.cellType !== ArsTableCellType.RowGroupCell &&
    action.cellValue.cellType !== ArsTableCellType.RowGroupLabel
  ) {
    return state;
  }

  // Get updates dataPath
  const targetDataPath = arst_resolveTargetPath(action);

  // Check if the target dataPath exists in the bodyData
  let targetValue = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
    isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
  );
  if (!targetValue) {
    return state;
  }

  // Get sibling count
  let siblingCount = action.cellValue.siblingCount;
  const isRemoveColumn = [
    ArsTableCellType.ColumnGroupCell,
    ArsTableCellType.ColumnGroupLabel,
  ].includes(action.cellValue.cellType);

  // Get grouping factor sibling count
  if (targetDataPath !== action.cellValue.dataPath) {
    targetValue = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
      isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
    );
  }
  // Refresh sibling count
  const parentDataPath = reduceDataPath(targetDataPath, 2);
  const parentGroup = getFromDataPath(parentDataPath, state.bodyData, (obj) =>
    isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
  );
  siblingCount =
    parentGroup?.subGroups?.length ??
    (isRemoveColumn ? state.bodyData.columns.length : state.bodyData.rows.length);
  // Remove grouping factor node from bodyData
  const newBodyData = removeByPath(state.bodyData, targetDataPath, siblingCount - 1);

  // Check if row headers need to be updated
  newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);

  // Check some operations are still present in the table, if not, clear the analysisMethodId
  if (!containsAnalysisMethod(newBodyData.columns) && !containsAnalysisMethod(newBodyData.rows)) {
    newBodyData.analysisMethodId = "";
  }

  return {
    ...state,
    bodyData: newBodyData,
    usedGroupingFactorIds: computeUsedGroupingFactorIds(newBodyData),
    changed: true,
  };
};
